/** @prettier */

import {useParams, Link} from 'react-router-dom';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogContentText,
  Grid,
  Tabs,
  Tab,
  Box,
  Stack,
  CircularProgress,
} from '@mui/material';
import {Dispatch, SetStateAction, useState} from 'react';
import {useTranslation} from 'react-i18next';

//MTH component import
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
import {CSVUploadButton} from '../../component/FileUploadButton';
import CollectiveSettingAddDialog from './CollectiveSettingAddDialog';
import CSVFormatErrorMessage from '../../component/CSVFormatErrorMessage';
import CSVNoDiffErrorMessage from '../../component/CSVNoDiffErrorMessage';
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {getCompositionsAllUnit} from '../../API/API_Collection';
//type import
import * as apiIF from '../../Type/api';
import * as Type from '../../Type/TypeDef';
//Style import
import '../../css/common/common.css';

import {
  convertCSVData,
  createDisplayData,
  convToIconName,
  convToModelName,
} from './CollectiveSettingFunction';

/**
 * 一括登録ダイアログ
 * @param props props
 * @returns JSX
 * - gwID
 * - gwName
 * - slType sl種
 * - isOpenDialog
 * - hasConfigGW
 * - setIsOpenDialog openのセット関数
 * - setHasConfigGW hasConfigGWのセット関数
 */
const CollectiveSettingDialog = (props: {
  /** ゲートウェイID */
  gwID: string;
  /** ゲートウェイ名 */
  gwName: string;
  /** 新旧設定。 */
  slType: 'new' | 'old';
  /** ダイアログ表示フラグ */
  isOpenDialog: boolean;
  /** ダイアログ表示フラグ（set関数） */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** CONFIG UPDATEボタンを表示するGW一覧 */
  hasConfigGW: string[];
  /** CONFIG UPDATEボタンを表示するGW一覧（set関数） */
  setHasConfigGW: Dispatch<SetStateAction<string[]>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID} = useParams();

  const gwID = props.gwID;
  const slType = props.slType;

  const [tabValue, setTabValue] = useState(0); // タブの状態を管理
  const [csvData, setCSVData] = useState<string>(''); //登録するユニットのCSVデータ
  const [displayNewData, setDisplayNewData] = useState<string>(''); //差分表示　設定する構成情報
  const [templateButtonDisabled, setTemplateButtonDisabled] = useState(false); //テンプレートファイル取得ボタンの非表示フラグ
  const [currentButtonDisabled, setCurrentButtonDisabled] = useState(false); //現在の設定ファイル取得ボタン非表示フラグ
  const [isOpenApplyDialog, setIsOpenApplyDialog] = useState(false); //確認ダイアログ表示フラグ
  const [applyButtonDisabled, setApplyButtonDisabled] = useState(true); //適用ボタン非表示フラグ
  /** 差分比較コンポーネントの状態と比較処理の状態を管理するためのインターフェイス */
  interface DifferenceStateInterface {
    /** コンポーネントが非表示かどうか */
    disabled: boolean;
    /** 処理中かどうか */
    loading: boolean;
  }
  //prettier-ignore
  //差分比較コンポーネントの状態と比較処理の状態を管理するための変数
  const [differenceState, setDifferenceState] = useState<DifferenceStateInterface>({disabled: true, loading: false});

  //エラーダイアログ関連
  //prettier-ignore
  const [csvFormatError, setCSVFormatError] = useState({errorCode: '', parameter: ['']}); //CSVフォーマットエラー
  //prettier-ignore
  const [isOpenCSVFormatErrorDialog, setIsOpenCSVFormatErrorDialog] = useState(false); //CSVフォーマットエラーダイアログ
  const [isOpenDiffErrorDialog, setIsOpenDiffErrorDialog] = useState(false); //差分比較エラーダイアログ
  const [isOpenAPIErrorDialog, setIsOpenAPIErrorDialog] = useState(false); //API使用時エラー発生ダイアログ表示フラグ
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  /**
   * タブの状態変更に合わせてボタンの状態をリセットする関数
   * タブの選択が変更されたときに呼び出され、関連するボタンの状態を初期化する
   * @param event - イベントオブジェクト（React.SyntheticEvent）。
   * @param newValue - 新しいタブの値（number型）
   */
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    //タブの状態の変更
    setTabValue(newValue);

    //差分比較コンポーネントを非表示
    resetDifferenceState();

    //適用ボタン無効化
    setApplyButtonDisabled(true);
  };

  /**
   * 差分比較コンポーネントを非表示
   */
  const resetDifferenceState = () => {
    setDifferenceState({disabled: true, loading: false});
  };

  /**
   * 差分比較コンポーネントを表示
   */
  const enableDifferenceState = () => {
    setDifferenceState({disabled: false, loading: false});
  };

  /**
   * 比較処理の状態をローディング状態
   */
  const loadingDifferenceState = () => {
    setDifferenceState({disabled: false, loading: true});
  };

  /**
   * CSVファイル選択ボタン押下時処理
   * @param file アップロードするCSVファイル
   * @returns
   */
  const uploadCSVSelect = (file: File) => {
    if (siteID === undefined) {
      return;
    }

    //初期状態に戻す
    resetDifferenceState();
    setApplyButtonDisabled(true);

    try {
      //ファイル選択時にフォーマットをチェックする仕様なのでこのファイル選択時にデータを取得する
      const reader = new FileReader();
      reader.readAsText(file);

      reader.onload = () => {
        const readData: string = reader.result as string;

        // データチェック&データ成型
        const returnData = convertCSVData(readData, slType);

        //CSVファイルのフォーマットエラーの場合の処理
        if ('errorCode' in returnData) {
          setCSVFormatError(returnData);
          setIsOpenCSVFormatErrorDialog(true);
          return;
        }

        //差分比較用にファイルデータを保存
        const newDisplay = createDisplayData(returnData);
        setDisplayNewData(newDisplay);

        // API送信用にデータを保存
        setCSVData(convToModelName(readData));

        //CheckChangeボタンを有効化
        enableDifferenceState();
      };
    } catch (error) {
      //ボタンの無効化
      resetDifferenceState();
      setApplyButtonDisabled(true);
    } finally {
      setAPIWait(false);
    }
  };

  /**
   * APPLYボタン押下時処理
   */
  const applyClickOpen = () => {
    if (siteID === undefined) {
      return;
    }
    setIsOpenApplyDialog(true);
    setApplyButtonDisabled(true);
  };

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    //タブの状態の初期化
    setTabValue(0);

    setApplyButtonDisabled(true);

    resetDifferenceState();

    props.setIsOpenDialog(false);
  };

  /**
   * ダウンロードボタン押下時の処理
   * APIから取得したデータを取得しCSVファイルを生成
   * @param contentValue ダウンロードするファイル
   * @returns
   */
  const downloadCSVFile = async (
    contentValue: apiIF.reqParameters.getCompositionsSLAllUnit['output-content']
  ) => {
    if (siteID === undefined) {
      return;
    }

    /** CSVファイルの名前 */
    const outputContentFileName = {
      /** 空調機の情報が含まれていない新規設定用のテンプレートファイル */
      nowTemplate: 'new_setting_template.csv',
      /** 現在の空調機の設定ファイル */
      currentSetting: 'current_setting.csv',
    };

    //引数に応じて対応するダウンロードボタンを無効化
    contentValue === 'template'
      ? setTemplateButtonDisabled(true)
      : setCurrentButtonDisabled(true);

    try {
      // 引数の値を元にAPIリクエストで使用するパラメータを生成
      const outputContentParam: apiIF.reqParameters.getCompositionsSLAllUnit = {
        'output-content': contentValue,
      };

      setAPIWait(true);

      const csvDataString = await getCompositionsAllUnit(
        siteID,
        gwID,
        outputContentParam
      );

      // 取得したデータから CSV ファイルを作成
      const blob = new Blob([convToIconName(csvDataString)], {
        type: 'text/csv',
      });

      // ダウンロードリンクの生成
      let link = document.createElement('a');
      link.href = URL.createObjectURL(blob);

      //引数からCSVファイルの名前を決める
      link.download =
        contentValue === 'template'
          ? outputContentFileName.nowTemplate
          : outputContentFileName.currentSetting;

      // リンクをクリックしてダウンロード
      link.click();
    } catch (error) {
      setIsOpenAPIErrorDialog(true);
    } finally {
      // 処理が完了したらボタンを有効化
      setTemplateButtonDisabled(false);
      setCurrentButtonDisabled(false);
      setAPIWait(false);
    }
  };

  /**
   * アップロードセクションのタイトル部分
   * @returns JSX.Element
   */
  const UploadSectionTitle = () => {
    return (
      <Box className="collectiveSettingDialog__uploadSection__title">
        {trans('CollectiveSettingDialog.upload.text.title')}
      </Box>
    );
  };

  /**
   * 現在の構成情報が入力されたファイルをダウンロードするためのボタン
   * @returns JSX.Element
   */
  const DownloadCurrentSettingFile = () => (
    <Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          {trans('CollectiveSettingDialog.download.text.current')}
        </Grid>
        <Grid item xs={3.5}>
          <Box className="collectiveSettingDialog__tabContents__unitType">
            {trans('CollectiveSettingDialog.download.text.airConditioner')}
          </Box>
        </Grid>
        <Grid item xs>
          <PrimaryButton
            onClick={() => downloadCSVFile('current-setting')}
            buttonText={trans(
              'CollectiveSettingDialog.download.button.download'
            )}
            disabled={currentButtonDisabled}
            apiWait={currentButtonDisabled}
          />
        </Grid>
      </Grid>
    </Box>
  );

  /**
   * サンプルの構成情報が入力されたファイルをダウンロードするためのボタン
   * @returns JSX.Element
   */
  const DownloadNowTemplate = () => (
    <Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          {trans('CollectiveSettingDialog.download.text.setting')}
        </Grid>
        <Grid item xs={3.5}>
          <Box className="collectiveSettingDialog__tabContents__unitType">
            {trans('CollectiveSettingDialog.download.text.airConditioner')}
          </Box>
        </Grid>
        <Grid item xs>
          <PrimaryButton
            onClick={() => downloadCSVFile('template')}
            buttonText={trans(
              'CollectiveSettingDialog.download.button.download'
            )}
            disabled={templateButtonDisabled}
            apiWait={templateButtonDisabled}
          />
        </Grid>
      </Grid>
    </Box>
  );

  /**
   * 差分比較ボタン
   */
  const DifferenceComparison = () => {
    const url = `/pop-out/my-site/site-detail/${siteID}/gw-setting/${gwID}/collective-setting-difference`;

    /**
     * 押下時の処理
     * @returns
     */
    const checkChangeClickOpen = async () => {
      try {
        if (siteID === undefined) {
          return;
        }
        //ボタンをローディング状態に
        loadingDifferenceState();

        setAPIWait(true);

        //現在の構成情報を取得してCSVファイル化する
        const csvDataString = await getCompositionsAllUnit(siteID, gwID);
        // データチェック&データ成型
        const returnData = convertCSVData(
          convToIconName(csvDataString),
          slType,
          true
        );

        if (
          'parameter' in returnData &&
          returnData.parameter.includes('initialize')
        ) {
          openDiffViewerNewTab(''); // 差分比較画面を旧データなしで開く
          setApplyButtonDisabled(false); // 適用ボタンを有効化
          return;
        }

        //表示文字列の作成
        //GetAPIで取得した値のフォーマットは正しい物として処理
        //元々のCSVファイルのフォーマットエラーチェックによるコンソール出力はエラー処理ではなく、
        //型推論によるエラーを回避するため
        const oldDisplay = createDisplayData(
          returnData as (
            | Type.CollectiveSettingData.collectiveAirconData
            | Type.CollectiveSettingData.collectiveInterfaceData
          )[]
        );
        //差分の有無で表示を切り替える
        if (displayNewData !== oldDisplay) {
          //差分ありの場合
          openDiffViewerNewTab(oldDisplay); // 差分比較画面を新しいタブで開く
          setApplyButtonDisabled(false); // 適用ボタンを有効化
        } else {
          setIsOpenDiffErrorDialog(true); //エラーダイアログ表示
          setApplyButtonDisabled(true); //適用ボタンを無効化
        }
      } catch (error) {
        //ボタンを初期状態に戻す
        resetDifferenceState();
        setApplyButtonDisabled(true);
        setIsOpenAPIErrorDialog(true);
      } finally {
        enableDifferenceState(); //チェックボタンのローディング状態を取り消し
        setAPIWait(false);
      }
    };

    /**
     * 差分比較画面を別タブで開く
     * @param oldData - 現在の空調機の設定データ
     */
    const openDiffViewerNewTab = (oldData: string) => {
      //差分比較用に現在の設定データとCSVファイル設定データをセッションストレージに保存
      sessionStorage.setItem(
        'diffData',
        JSON.stringify({
          displayOldData: oldData,
          displayNewData: displayNewData,
        })
      );

      //差分比較画面を別タブで開く。差分比較用のデータはセッションストレージから取得
      window.open(url, '_blank');
    };

    return (
      <Box hidden={differenceState.disabled}>
        <Grid container alignItems="center" columnSpacing={0.5}>
          <Grid
            item
            xs={11}
            className="collectiveSettingDialog__uploadSection__text">
            <Link
              to={url}
              onClick={e => {
                e.preventDefault();
                checkChangeClickOpen();
              }}
              target="_blank"
              rel="noopener noreferrer">
              {trans('CollectiveSettingDialog.upload.text.checkChange')}
            </Link>
          </Grid>
          <Grid
            item
            xs={1}
            className="collectiveSettingDialog__uploadSection__icon">
            {differenceState.loading && <CircularProgress size={24} />}
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <>
      <Dialog open={props.isOpenDialog} onClose={handleClose}>
        <DialogTitle>{trans('CollectiveSettingDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText className="collectiveSettingDialog__description">
            {trans('CollectiveSettingDialog.description')}
          </DialogContentText>
          <Stack spacing={2}>
            {/** タブ */}
            <Box className="collectiveSettingDialog__tab">
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="collective setting of units tab">
                <Tab label={trans('CollectiveSettingDialog.tab.download')} />
                <Tab label={trans('CollectiveSettingDialog.tab.upload')} />
              </Tabs>
            </Box>
            {/** ダウンロードタブ */}
            <Box
              hidden={tabValue !== 0}
              className="collectiveSettingDialog__tabContents">
              <Stack spacing={1}>
                <DownloadCurrentSettingFile />
                <DownloadNowTemplate />
              </Stack>
            </Box>
            {/** アップロードタブ */}
            {/** クリックイベントが伝播しないのでコンポーネントの分割はしない*/}
            <Box
              hidden={tabValue !== 1}
              className="collectiveSettingDialog__tabContents">
              <Grid container spacing={2} alignItems="center">
                {/** タイトル */}
                <Grid item xs={12}>
                  {trans('CollectiveSettingDialog.upload.text.title')}
                </Grid>
                {/** コンテンツ */}
                <Grid item xs={3.5}>
                  <Box className="collectiveSettingDialog__tabContents__unitType">
                    {trans(
                      'CollectiveSettingDialog.upload.text.airConditioner'
                    )}
                  </Box>
                </Grid>
                <Grid item xs>
                  <Stack>
                    {/** CSVファイル選択ボタン */}
                    <Box className="collectiveSettingDialog__tabContents__button">
                      <CSVUploadButton
                        setCSVFunc={uploadCSVSelect}
                        id="mv-ConfigCSVSelect"
                      />
                    </Box>
                    {/** 差分比較ボタン */}
                    <DifferenceComparison />
                  </Stack>
                </Grid>
              </Grid>
              {/** 確認ダイアログ */}
              <CollectiveSettingAddDialog
                open={isOpenApplyDialog}
                setOpen={setIsOpenApplyDialog}
                setApplyButton={setApplyButtonDisabled}
                setParentClose={handleClose}
                csvData={csvData}
                gwID={gwID}
                hasConfigGW={props.hasConfigGW}
                setHasConfigGW={props.setHasConfigGW}
              />
              {/** ファイル差分なしエラーダイアログ */}
              <CSVNoDiffErrorMessage
                open={isOpenDiffErrorDialog}
                setOpen={setIsOpenDiffErrorDialog}
                id="mv-btnErrDiff"
              />
              {/** ファイル形式エラーダイアログ */}
              <CSVFormatErrorMessage
                open={isOpenCSVFormatErrorDialog}
                setOpen={setIsOpenCSVFormatErrorDialog}
                errorMessage={csvFormatError}
                id="mv-btnErrFormat"
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          {/** キャンセルボタン */}
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('CollectiveSettingDialog.button.cancel')}
            id={`mv-btnCancelGwSettingEdit`}
          />
          {/** 適用ボタン */}
          <Box hidden={tabValue === 0}>
            <PrimaryButton
              onClick={applyClickOpen}
              buttonText={trans('CollectiveSettingDialog.upload.button.apply')}
              disabled={applyButtonDisabled}
              apiWait={false}
            />
          </Box>
        </DialogActions>
      </Dialog>
      {/** APIエラーダイアログ */}
      <APIErrorMessage
        open={isOpenAPIErrorDialog}
        setOpen={setIsOpenAPIErrorDialog}
        id="mv-btnErrApi"
      />
    </>
  );
};

export default CollectiveSettingDialog;
